/**
 * «Праздники» (13.10.2026): баннер государственного праздника на Главной
 * приложения. Даты приходят из календаря праздников РК сами, здесь у каждого
 * праздника задаётся картинка, за сколько дней показывать, поздравление в сам
 * день и «показывать ли». Приложение подхватывает правки в течение часа.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputNumber, Popconfirm, Space, Switch, Table, Tooltip, Typography, Upload, message } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { holidaysApi, HolidayRow, HolidayUpdate } from '../../api/holidays';

const { Title, Text } = Typography;

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

const humanDate = (iso: string | null) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

const errText = (e: unknown) =>
  (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Не получилось';

/** Превью как на Главной: картинка, снизу затемнение, слева внизу две строки. */
const Preview: React.FC<{ r: HolidayRow }> = ({ r }) => {
  const name = r.name_ru || r.local_name || r.key;
  const d = r.next_date ? humanDate(r.next_date)!.replace(/ \d{4}$/, '') : '';
  return (
    <div
      style={{
        width: 240, height: 100, borderRadius: 12, overflow: 'hidden', position: 'relative',
        background: r.image_url
          ? `url(${r.image_url}) center / cover no-repeat`
          : 'linear-gradient(180deg, #070B16 0%, #0F1B36 60%, #16305C 100%)',
        opacity: r.is_enabled ? 1 : 0.4,
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(3,20,40,.85) 0%, rgba(3,20,40,.25) 55%, rgba(3,20,40,0) 80%)' }} />
      <div style={{ position: 'absolute', left: 10, right: 10, bottom: 8, color: '#F2F5FA', lineHeight: 1.25 }}>
        <div style={{ fontWeight: 600, fontSize: 12 }}>{d ? `${d}, ${name}` : name}</div>
        <div style={{ fontSize: 11, opacity: 0.85 }}>Через {r.days_before} дн. · в сам день: {r.greeting_ru || 'С праздником!'}</div>
      </div>
    </div>
  );
};

const HolidaysPage: React.FC = () => {
  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await holidaysApi.list();
      setRows(res.data.items);
    } catch {
      message.error('Не удалось загрузить праздники');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusyKey(key);
    try {
      await fn();
      message.success(ok);
      await load();
    } catch (e) {
      message.error(errText(e));
    } finally {
      setBusyKey(null);
    }
  };

  const save = (r: HolidayRow, data: HolidayUpdate) => run(r.key, () => holidaysApi.update(r.key, data), 'Сохранено');

  const columns = [
    {
      title: 'Как увидит житель',
      key: 'preview',
      width: 300,
      render: (_: unknown, r: HolidayRow) => (
        <Space direction="vertical" size={6}>
          <Preview r={r} />
          <Space size={6}>
            <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              beforeUpload={(file) => {
                void run(r.key, () => holidaysApi.uploadImage(r.key, file as File), 'Картинка загружена');
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />} loading={busyKey === r.key}>
                {r.image_url ? 'Заменить картинку' : 'Загрузить картинку'}
              </Button>
            </Upload>
            {r.image_url ? (
              <Popconfirm title="Убрать картинку? Останется тёмный фон с текстом." onConfirm={() => run(r.key, () => holidaysApi.clearImage(r.key), 'Картинка убрана')}>
                <Tooltip title="Убрать картинку">
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={busyKey === r.key} />
                </Tooltip>
              </Popconfirm>
            ) : null}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Праздник',
      key: 'name',
      render: (_: unknown, r: HolidayRow) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name_ru || r.local_name || r.key}</div>
          {r.local_name ? <Text type="secondary">{r.local_name}</Text> : null}
          <div style={{ marginTop: 4 }}>
            {r.dates.length ? (
              <Text>{r.dates.map(humanDate).join(', ')}</Text>
            ) : (
              <Text type="secondary">нет в календаре на ближайший год</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Показывать за',
      key: 'days',
      width: 130,
      render: (_: unknown, r: HolidayRow) => (
        <InputNumber
          min={0}
          max={14}
          defaultValue={r.days_before}
          addonAfter="дн."
          style={{ width: 110 }}
          disabled={busyKey === r.key}
          onBlur={(e) => {
            const v = Number((e.target as HTMLInputElement).value);
            if (Number.isFinite(v) && v !== r.days_before && v >= 0 && v <= 14) void save(r, { days_before: v });
          }}
        />
      ),
    },
    {
      title: 'Поздравление в сам день',
      key: 'greeting',
      width: 320,
      render: (_: unknown, r: HolidayRow) => (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Input
            addonBefore="RU"
            defaultValue={r.greeting_ru ?? ''}
            placeholder="С праздником!"
            maxLength={120}
            onBlur={(e) => { if ((e.target.value || '') !== (r.greeting_ru ?? '')) void save(r, { greeting_ru: e.target.value }); }}
          />
          <Input
            addonBefore="KZ"
            defaultValue={r.greeting_kk ?? ''}
            placeholder={`${r.local_name || 'Мереке'} құтты болсын!`}
            maxLength={120}
            onBlur={(e) => { if ((e.target.value || '') !== (r.greeting_kk ?? '')) void save(r, { greeting_kk: e.target.value }); }}
          />
        </Space>
      ),
    },
    {
      title: 'Показывать',
      key: 'enabled',
      width: 110,
      render: (_: unknown, r: HolidayRow) => (
        <Switch checked={r.is_enabled} loading={busyKey === r.key} onChange={(v) => void save(r, { is_enabled: v })} />
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>Праздники</Title>
      <Text type="secondary">
        Баннер государственного праздника на Главной приложения. Появляется за указанное число дней («Через 2 дня»,
        «Завтра»), в сам день вместо этого поздравление. Картинка горизонтальная, от 1200×500: внизу слева ляжет текст,
        главное держите в центре и справа. В приложении изменения появятся в течение часа.
      </Text>
      <Table
        style={{ marginTop: 16 }}
        rowKey={(r) => `${r.key}|${r.updated_at ?? ''}`}
        dataSource={rows}
        columns={columns}
        loading={loading}
        pagination={false}
        size="middle"
      />
    </div>
  );
};

export default HolidaysPage;
